// AsyncStorage-backed outbox for session-telemetry batches (ED-2572).
//
// The recorder's buffer is process memory; an app kill, crash, or extended
// offline stretch loses the session tail. The outbox persists each session's
// undelivered events WITH the session meta they belong to, so the next launch
// can deliver them under their ORIGINAL session id — the server's ownership
// guard accepts them (same user), (session_id, seq) dedupes anything that
// actually made it, and journeys stay stitched to the session they happened
// in rather than being grafted onto the new one.
//
// Storage model (WatchNoteQueue precedent, simplified): one key holding an
// array of envelopes, newest last. Caps favor recency — telemetry is
// best-effort, and six sessions of backlog means something is wrong upstream.
// Every write is fire-and-forget: persistence must never block or throw into
// the recorder's callers.

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { TelemetryEvent } from './recorder';

export interface OutboxSessionMeta {
  id: string;
  platform: string;
  app_version: string | null;
  os_version: string;
  timezone: string | null;
  locale: string | null;
  started_at: string;
}

export interface OutboxEnvelope {
  meta: OutboxSessionMeta;
  events: TelemetryEvent[];
}

const KEY = '@eleplan/telemetry_outbox';
const MAX_ENVELOPES = 6;
const MAX_EVENTS_TOTAL = 600;

function enforceCaps(envelopes: OutboxEnvelope[]): OutboxEnvelope[] {
  let out = envelopes.filter((e) => e && e.meta?.id && Array.isArray(e.events) && e.events.length > 0);
  if (out.length > MAX_ENVELOPES) out = out.slice(out.length - MAX_ENVELOPES);
  let total = out.reduce((n, e) => n + e.events.length, 0);
  while (total > MAX_EVENTS_TOTAL && out.length > 1) {
    total -= out[0].events.length; // oldest session pays first
    out = out.slice(1);
  }
  if (total > MAX_EVENTS_TOTAL && out.length === 1) {
    out = [{ ...out[0], events: out[0].events.slice(out[0].events.length - MAX_EVENTS_TOTAL) }];
  }
  return out;
}

export async function loadOutbox(): Promise<OutboxEnvelope[]> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? enforceCaps(parsed) : [];
  } catch {
    return []; // corrupt/unreadable — start clean
  }
}

export function saveOutbox(envelopes: OutboxEnvelope[]): void {
  try {
    const capped = enforceCaps(envelopes);
    if (!capped.length) {
      void AsyncStorage.removeItem(KEY).catch(() => {});
      return;
    }
    void AsyncStorage.setItem(KEY, JSON.stringify(capped)).catch(() => {});
  } catch {
    // serialization failure — drop rather than throw
  }
}

/** Replace the envelope for `envelope.meta.id` (or append), newest last. */
export function upsertEnvelope(envelopes: OutboxEnvelope[], envelope: OutboxEnvelope): OutboxEnvelope[] {
  return [...envelopes.filter((e) => e.meta.id !== envelope.meta.id), envelope];
}

export function removeEnvelope(envelopes: OutboxEnvelope[], sessionId: string): OutboxEnvelope[] {
  return envelopes.filter((e) => e.meta.id !== sessionId);
}
